import React, { useEffect, useState } from 'react';
import { GraduationCap, Briefcase, Tree, Hammer, PaperPlaneTilt, Globe } from '@phosphor-icons/react';

import Progress from '../../components/ui/progress';
import { EXPERIENCE_TRANSITION_MS, LIFE_CHAPTERS } from '../content';
import {
  COMPLETE,
  TRANSITIONING,
  advanceExperience,
  createExperienceState,
  resetExperience,
  selectExperienceOption,
} from '../state/experienceState';
import useTransitionFocus from '../hooks/useTransitionFocus';
import NarrativeChapter from './NarrativeChapter';
import ResonanceOrb from './ResonanceOrb';

const LIFE_CHAPTER_ICONS = {
  'student': <GraduationCap className="chapter-icon" size={18} />,
  'early-career': <Briefcase className="chapter-icon" size={18} />,
  'established': <Tree className="chapter-icon" size={18} />,
  'building': <Hammer className="chapter-icon" size={18} />,
  'transitioning': <PaperPlaneTilt className="chapter-icon" size={18} />,
  'nomadic': <Globe className="chapter-icon" size={18} />,
};

const LIFE_PREVIEW_STEPS = 2;

/**
 * Renders the adaptive life-chapter preview. Accepts no props.
 *
 * @returns {React.ReactElement} Chapter four with all six phases initially,
 *   then only the selected phase's prompt and three follow-up choices.
 * @sideEffects Schedules and clears one browser timeout after each valid
 *   choice, and restores focus after prompt replacement.
 */
export default function LifeChapterPreview({ isActive, index = 6 }) {
  const [preview, setPreview] = useState(() => ({
    experience: createExperienceState(LIFE_PREVIEW_STEPS),
    selectedChapter: null,
  }));
  const { experience: state, selectedChapter } = preview;
  const promptRegionRef = useTransitionFocus(state.phase);

  useEffect(() => {
    if (!isActive) {
      setPreview({
        experience: createExperienceState(LIFE_PREVIEW_STEPS),
        selectedChapter: null,
      });
    }
  }, [isActive]);

  useEffect(() => {
    if (state.phase !== TRANSITIONING) {
      return undefined;
    }

    const transitionTimer = window.setTimeout(() => {
      setPreview((latest) => ({
        ...latest,
        experience: advanceExperience(latest.experience),
      }));
    }, EXPERIENCE_TRANSITION_MS);

    return () => window.clearTimeout(transitionTimer);
  }, [state.phase]);

  const beginTransition = (value, chapter = null) => {
    setPreview((current) => {
      const selected = selectExperienceOption(current.experience, value);

      return selected === current.experience ? current : {
        experience: selected,
        selectedChapter: chapter || current.selectedChapter,
      };
    });
  };

  const handleChapterSelect = (chapter) => beginTransition(chapter.id, chapter);


  const isComplete = state.phase === COMPLETE;
  const isTransitioning = state.phase === TRANSITIONING;
  const progress = (state.index / state.total) * 100;

  return (
    <NarrativeChapter
      index={index}
      id="life-chapter"
      title="Your profile follows your chapter."
      body={isComplete ? null : "Showcase who you are based on the phase of life you are currently in."}
    >
      <div
        className="experience-preview life-chapter-preview"
        data-selected-chapter={selectedChapter?.id || ''}
      >
        {isComplete ? null : (
          <Progress
            value={progress}
            current={state.index}
            total={state.total}
            label="Chapter"
          />
        )}
        <div
          ref={promptRegionRef}
          className={`experience-preview__prompt experience-preview__prompt--${state.phase}`}
          aria-live="polite"
          aria-atomic="true"
          tabIndex={-1}
        >
          {isComplete ? (
            <div className="experience-preview__complete">
              <div className="experience-preview__complete-content">
                <p>Life isn't a static profile. It's a chapter in motion.</p>
                <ResonanceOrb iconType="compass" />
              </div>
            </div>
          ) : state.index === 0 ? (
            <>
              <p>Which chapter feels closest to now?</p>
              <div className="experience-preview__options">
                {LIFE_CHAPTERS.map((chapter) => (
                  <button
                    key={state.index + '-' + chapter.id}
                    type="button"
                    disabled={isTransitioning}
                    onClick={() => handleChapterSelect(chapter)}
                  >
                    {LIFE_CHAPTER_ICONS[chapter.id]}
                    {chapter.label}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <p>{selectedChapter.prompt}</p>
              <div className="experience-preview__options">
                {selectedChapter.options.map((option) => (
                  <button
                    key={state.index + '-' + option}
                    type="button"
                    disabled={isTransitioning}
                    onClick={() => beginTransition(option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </NarrativeChapter>
  );
}
